import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { authApi } from '@/utils/api'
import type { InvitationPerson } from '@/utils/api'
import { useAuth } from '@/context/AuthContext'
import Header from '@/components/Header'

type Step = 'loading' | 'select-person' | 'verify-otp' | 'create-password'

export default function AcceptInvitationPage() {
  const { coupleId } = useParams<{ coupleId: string }>()
  const navigate = useNavigate()
  const { verifyOtp, createPassword } = useAuth()

  const [step, setStep] = useState<Step>('loading')
  const [persons, setPersons] = useState<[InvitationPerson, InvitationPerson] | null>(null)
  const [selectedPerson, setSelectedPerson] = useState<InvitationPerson | null>(null)
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)

  // Fetch invitation info on mount
  useEffect(() => {
    if (!coupleId) {
      setError('Missing invitation id.')
      return
    }
    ;(async () => {
      try {
        const data = await authApi.getInvitationInfo(coupleId) as {
          personOne?: InvitationPerson
          personTwo?: InvitationPerson
        }
        if (data?.personOne && data?.personTwo) {
          setPersons([data.personOne, data.personTwo])
          setStep('select-person')
        } else {
          setError('Unable to load invitation details.')
        }
      } catch {
        setError('Unable to load invitation details. The link may be invalid or expired.')
      }
    })()
  }, [coupleId])

  const handleSelectPerson = async (person: InvitationPerson) => {
    setSelectedPerson(person)
    setError(null)
    setMessage(null)

    // Send OTP to the selected person's phone
    setLoading(true)
    try {
      await authApi.sendOtp(person.phoneNumber)
      setStep('verify-otp')
      setMessage('OTP sent to your phone.')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to send OTP.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedPerson) return
    setError(null)
    setMessage(null)
    setLoading(true)
    try {
      const res = await verifyOtp({ phoneNumber: selectedPerson.phoneNumber, otp })
      setUserId(res.userId ?? selectedPerson.userId)
      setStep('create-password')
    } catch (err) {
      setError((err as Error)?.message || 'Invalid or expired OTP.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setMessage(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    const id = userId ?? selectedPerson?.userId
    if (!id) {
      setError('Missing user information.')
      return
    }

    setLoading(true)
    try {
      await createPassword({ userId: id, password, confirmPassword })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError((err as Error)?.message || 'Unable to create password.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (!selectedPerson) return
    setResending(true)
    setError(null)
    setMessage(null)
    try {
      await authApi.sendOtp(selectedPerson.phoneNumber)
      setMessage('OTP resent. Check your phone.')
    } catch (err) {
      setError((err as Error)?.message || 'Unable to resend OTP.')
    } finally {
      setResending(false)
    }
  }

  const maskedPhone = selectedPerson
    ? selectedPerson.phoneNumber.slice(0, 4) + '****' + selectedPerson.phoneNumber.slice(-3)
    : ''

  const personDisplayName = (p: InvitationPerson) => {
    if (p.firstName || p.lastName) return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()
    return p.email ?? p.phoneNumber
  }

  const maskedPhoneFor = (p: InvitationPerson) =>
    p.phoneNumber.slice(0, 4) + '****' + p.phoneNumber.slice(-3)

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      <Header />
      <div className='flex-1 flex items-center justify-center px-4 py-10'>
        <div className='w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-6'>
          <div className='space-y-1 text-start'>
            <p className='text-xs uppercase tracking-wide text-orange-500 font-semibold'>Couple Invite</p>
            <h1 className='text-2xl font-bold text-gray-900'>Accept Invitation</h1>
            <p className='text-sm text-gray-600'>
              {step === 'loading' && 'Loading invitation details...'}
              {step === 'select-person' && 'Select your profile to continue.'}
              {step === 'verify-otp' && `Enter the OTP sent to ${maskedPhone}.`}
              {step === 'create-password' && 'Set your password to complete onboarding.'}
            </p>
          </div>

          {/* Error / message banner */}
          {(error || message) && (
            <div
              className={`text-sm rounded-lg px-4 py-3 ${
                error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}
            >
              {error || message}
            </div>
          )}

          {/* Step 1: Select person */}
          {step === 'select-person' && persons && (
            <div className='space-y-3'>
              {persons.map((person, idx) => (
                <button
                  key={person.userId ?? idx}
                  type='button'
                  disabled={loading}
                  onClick={() => handleSelectPerson(person)}
                  className='w-full text-left rounded-xl border border-gray-200 p-4 hover:border-orange-400 hover:bg-orange-50 transition-colors disabled:opacity-60'
                >
                  <p className='font-semibold text-gray-900'>{personDisplayName(person)}</p>
                  <p className='text-sm text-gray-500'>{maskedPhoneFor(person)}</p>
                  {person.email && (
                    <p className='text-sm text-gray-500'>{person.email}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Verify OTP */}
          {step === 'verify-otp' && (
            <form onSubmit={handleVerifyOtp} className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>OTP</label>
                <div className='flex gap-2'>
                  <input
                    type='text'
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder='6-digit code'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                    required
                  />
                  <button
                    type='button'
                    onClick={handleResendOtp}
                    disabled={resending}
                    className='px-3 py-2.5 text-sm font-semibold text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 disabled:opacity-60 whitespace-nowrap'
                  >
                    {resending ? 'Resending...' : 'Resend'}
                  </button>
                </div>
              </div>
              <button
                type='submit'
                disabled={loading}
                className='w-full py-3 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors disabled:opacity-60'
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>
          )}

          {/* Step 3: Create password */}
          {step === 'create-password' && (
            <form onSubmit={handleCreatePassword} className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Password</label>
                  <input
                    type='password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Confirm Password</label>
                  <input
                    type='password'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                    required
                  />
                </div>
              </div>
              <button
                type='submit'
                disabled={loading}
                className='w-full py-3 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors disabled:opacity-60'
              >
                {loading ? 'Completing...' : 'Complete Onboarding'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
